import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Megaphone, Plus, Pencil, Trash2, Eye, EyeOff, BarChart3,
  MessageSquare, PanelRight, Image, LayoutList, CalendarDays, Store, LayoutDashboard,
  ChevronDown, ChevronUp, X, Sparkles
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEMENT_OPTIONS = [
  { id: "popup", label: "Pop-up du site", icon: MessageSquare },
  { id: "sidebar_right", label: "Colonne publicitaire droite", icon: PanelRight },
  { id: "homepage_banner", label: "Banniere d'accueil", icon: Image },
  { id: "inline_block", label: "Bloc integre dans une page", icon: LayoutList },
  { id: "event_center", label: "Event Center", icon: CalendarDays },
  { id: "marketplace", label: "Marketplace / annuaire", icon: Store },
  { id: "organizer_dashboard", label: "Espace organisateur", icon: LayoutDashboard },
];

const STATUS_COLORS = {
  draft: "bg-gray-500/20 text-gray-300",
  active: "bg-emerald-500/20 text-emerald-400",
  paused: "bg-amber-500/20 text-amber-400",
  archived: "bg-red-500/20 text-red-400",
};

export default function AdCampaignsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statsView, setStatsView] = useState(null);

  const [form, setForm] = useState({
    name: "", advertiser: "GLOTECH DIGITAL", title: "", subtitle: "", description: "",
    image_url: "", cta_text: "En savoir plus", cta_url: "",
    start_date: "", end_date: "", status: "draft", priority: 5, display_order: 0,
    placements: ["sidebar_right"], target_pages: ["all"], audience: "all",
    is_internal: true, is_sponsored: false,
    popup_delay_seconds: 3, popup_frequency: "once_per_day", popup_close_allowed: true,
    popup_reappear_hours: 24, popup_show_mobile: true, popup_show_desktop: true,
    services: [],
  });
  const [serviceInput, setServiceInput] = useState("");

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ad-campaigns`, { headers });
      setCampaigns(data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const resetForm = () => {
    setForm({
      name: "", advertiser: "GLOTECH DIGITAL", title: "", subtitle: "", description: "",
      image_url: "", cta_text: "En savoir plus", cta_url: "",
      start_date: "", end_date: "", status: "draft", priority: 5, display_order: 0,
      placements: ["sidebar_right"], target_pages: ["all"], audience: "all",
      is_internal: true, is_sponsored: false,
      popup_delay_seconds: 3, popup_frequency: "once_per_day", popup_close_allowed: true,
      popup_reappear_hours: 24, popup_show_mobile: true, popup_show_desktop: true,
      services: [],
    });
    setEditing(null);
    setServiceInput("");
  };

  const handleSave = async () => {
    if (!form.name || !form.title) return toast.error("Nom et titre requis");
    try {
      if (editing) {
        await axios.put(`${API}/api/ad-campaigns/${editing}`, form, { headers });
        toast.success("Campagne mise a jour");
      } else {
        await axios.post(`${API}/api/ad-campaigns`, form, { headers });
        toast.success("Campagne creee");
      }
      setShowForm(false);
      resetForm();
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleEdit = (c) => {
    setForm({
      name: c.name || "", advertiser: c.advertiser || "GLOTECH DIGITAL",
      title: c.title || "", subtitle: c.subtitle || "", description: c.description || "",
      image_url: c.image_url || "", cta_text: c.cta_text || "En savoir plus", cta_url: c.cta_url || "",
      start_date: c.start_date || "", end_date: c.end_date || "",
      status: c.status || "draft", priority: c.priority || 5, display_order: c.display_order || 0,
      placements: c.placements || ["sidebar_right"], target_pages: c.target_pages || ["all"],
      audience: c.audience || "all", is_internal: c.is_internal !== false, is_sponsored: c.is_sponsored || false,
      popup_delay_seconds: c.popup_delay_seconds || 3, popup_frequency: c.popup_frequency || "once_per_day",
      popup_close_allowed: c.popup_close_allowed !== false, popup_reappear_hours: c.popup_reappear_hours || 24,
      popup_show_mobile: c.popup_show_mobile !== false, popup_show_desktop: c.popup_show_desktop !== false,
      services: c.services || [],
    });
    setEditing(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    try {
      await axios.delete(`${API}/api/ad-campaigns/${id}`, { headers });
      toast.success("Campagne supprimee");
      fetchCampaigns();
    } catch { toast.error("Erreur de suppression"); }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === "active" ? "paused" : "active";
    try {
      await axios.put(`${API}/api/ad-campaigns/${c.id}`, { status: newStatus }, { headers });
      toast.success(`Campagne ${newStatus === "active" ? "activee" : "mise en pause"}`);
      fetchCampaigns();
    } catch { toast.error("Erreur"); }
  };

  const togglePlacement = (p) => {
    setForm(prev => ({
      ...prev,
      placements: prev.placements.includes(p)
        ? prev.placements.filter(x => x !== p)
        : [...prev.placements, p]
    }));
  };

  const addService = () => {
    if (!serviceInput.trim()) return;
    setForm(prev => ({ ...prev, services: [...prev.services, serviceInput.trim()] }));
    setServiceInput("");
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  if (loading) return <div className="p-8 text-center text-[#94A3B8]">Chargement...</div>;

  return (
    <div data-testid="ad-campaigns-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-amber-500/10 border border-amber-500/20">
            <Megaphone className="h-6 w-6 text-[#F59E0B]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Regie Publicitaire</h1>
            <p className="text-sm text-[#64748b]">{campaigns.length} campagne{campaigns.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="seed-launch-campaign-btn"
            variant="outline"
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/api/ad-campaigns/seed-launch`, {}, { headers });
                toast.success(data.message);
                fetchCampaigns();
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
            }}
            className="text-sm border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          >
            <Sparkles className="h-4 w-4 mr-1" /> Lancement Event Center
          </Button>
          <Button
            data-testid="create-campaign-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-[#0099CC] hover:bg-[#0088B5]"
          >
            <Plus className="h-4 w-4 mr-1" /> Nouvelle campagne
          </Button>
        </div>
      </div>

      {/* Campaign Form Modal */}
      {showForm && (
        <div className="bg-[#0F1420] border border-[#2A3441] rounded-2xl p-6 space-y-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">{editing ? "Modifier la campagne" : "Nouvelle campagne"}</h2>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#94A3B8] hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Nom de campagne *</label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Lancement Event Center" className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Annonceur</label>
              <Input value={form.advertiser} onChange={e => set("advertiser", e.target.value)} className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Titre *</label>
              <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Vous organisez un evenement ?" className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Sous-titre / Badge</label>
              <Input value={form.subtitle} onChange={e => set("subtitle", e.target.value)} placeholder="OFFRE DE LANCEMENT" className="bg-[#151B2B] border-[#2A3441]" />
            </div>
          </div>

          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Description</label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="bg-[#151B2B] border-[#2A3441]" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">URL Image</label>
              <Input value={form.image_url} onChange={e => set("image_url", e.target.value)} placeholder="https://..." className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Texte CTA</label>
              <Input value={form.cta_text} onChange={e => set("cta_text", e.target.value)} className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">URL destination</label>
              <Input value={form.cta_url} onChange={e => set("cta_url", e.target.value)} placeholder="/evenements" className="bg-[#151B2B] border-[#2A3441]" />
            </div>
          </div>

          {/* Placements */}
          <div>
            <label className="text-xs text-[#94A3B8] mb-2 block">Emplacements de diffusion</label>
            <div className="flex flex-wrap gap-2">
              {PLACEMENT_OPTIONS.map(p => {
                const Icon = p.icon;
                const selected = form.placements.includes(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => togglePlacement(p.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                      selected
                        ? "bg-[#0099CC]/20 border-[#0099CC]/50 text-[#0099CC]"
                        : "bg-[#151B2B] border-[#2A3441] text-[#94A3B8] hover:border-[#4A5568]"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Popup settings - only when popup placement selected */}
          {form.placements.includes("popup") && (
            <div className="bg-[#151B2B]/50 border border-[#2A3441] rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-[#0099CC]" /> Parametres Pop-up
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-[#64748b]">Delai (sec)</label>
                  <Input type="number" value={form.popup_delay_seconds} onChange={e => set("popup_delay_seconds", parseInt(e.target.value) || 0)} className="bg-[#0F1420] border-[#2A3441] h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-[#64748b]">Frequence</label>
                  <select value={form.popup_frequency} onChange={e => set("popup_frequency", e.target.value)} className="w-full bg-[#0F1420] border border-[#2A3441] rounded-md px-2 h-9 text-sm text-white">
                    <option value="once">1 seule fois</option>
                    <option value="once_per_session">1x / session</option>
                    <option value="once_per_day">1x / jour</option>
                    <option value="always">Toujours</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#64748b]">Reapparition (h)</label>
                  <Input type="number" value={form.popup_reappear_hours} onChange={e => set("popup_reappear_hours", parseInt(e.target.value) || 24)} className="bg-[#0F1420] border-[#2A3441] h-9 text-sm" />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                    <input type="checkbox" checked={form.popup_show_mobile} onChange={e => set("popup_show_mobile", e.target.checked)} className="rounded" /> Mobile
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                    <input type="checkbox" checked={form.popup_show_desktop} onChange={e => set("popup_show_desktop", e.target.checked)} className="rounded" /> Desktop
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Services */}
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Services / Prestations</label>
            <div className="flex gap-2 mb-2">
              <Input value={serviceInput} onChange={e => setServiceInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addService())} placeholder="Ex: Sonorisation professionnelle" className="bg-[#151B2B] border-[#2A3441]" />
              <Button onClick={addService} variant="outline" size="sm" className="border-[#2A3441]">Ajouter</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.services.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-[#0099CC]/10 border border-[#0099CC]/20 text-[#0099CC] text-xs px-2.5 py-1 rounded-full">
                  {s}
                  <button onClick={() => setForm(p => ({ ...p, services: p.services.filter((_, j) => j !== i) }))} className="hover:text-red-400"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>

          {/* Scheduling & Status */}
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Statut</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full bg-[#151B2B] border border-[#2A3441] rounded-md px-3 h-10 text-sm text-white">
                <option value="draft">Brouillon</option>
                <option value="active">Active</option>
                <option value="paused">En pause</option>
                <option value="archived">Archivee</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Priorite (1-10)</label>
              <Input type="number" min={1} max={10} value={form.priority} onChange={e => set("priority", parseInt(e.target.value) || 5)} className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Date debut</label>
              <Input type="date" value={form.start_date?.split("T")[0] || ""} onChange={e => set("start_date", e.target.value ? new Date(e.target.value).toISOString() : "")} className="bg-[#151B2B] border-[#2A3441]" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1 block">Date fin</label>
              <Input type="date" value={form.end_date?.split("T")[0] || ""} onChange={e => set("end_date", e.target.value ? new Date(e.target.value).toISOString() : "")} className="bg-[#151B2B] border-[#2A3441]" />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
              <input type="checkbox" checked={form.is_internal} onChange={e => set("is_internal", e.target.checked)} className="rounded" />
              Campagne interne GLOTECH
            </label>
            <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
              <input type="checkbox" checked={form.is_sponsored} onChange={e => set("is_sponsored", e.target.checked)} className="rounded" />
              Sponsorisee
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }} className="border-[#2A3441]">Annuler</Button>
            <Button onClick={handleSave} className="bg-[#0099CC] hover:bg-[#0088B5]">
              {editing ? "Mettre a jour" : "Creer la campagne"}
            </Button>
          </div>
        </div>
      )}

      {/* Campaign List */}
      <div className="space-y-3">
        {campaigns.length === 0 && !showForm && (
          <div className="text-center py-16 text-[#64748b]">
            <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Aucune campagne</p>
            <p className="text-sm">Creez votre premiere campagne publicitaire ou utilisez le bouton "Lancement Event Center".</p>
          </div>
        )}

        {campaigns.map(c => {
          const isExpanded = statsView === c.id;
          const globalImpressions = c.stats?.global?.impressions || 0;
          const globalClicks = c.stats?.global?.clicks || 0;
          const globalCtr = globalImpressions > 0 ? ((globalClicks / globalImpressions) * 100).toFixed(1) + "%" : "—";

          return (
          <div key={c.id} className="bg-[#0F1420] border border-[#2A3441] rounded-xl p-5 hover:border-[#4A5568] transition-all">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-white truncate">{c.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                    {c.status === "active" ? "Active" : c.status === "paused" ? "En pause" : c.status === "draft" ? "Brouillon" : "Archivee"}
                  </span>
                  {c.is_sponsored && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B]">Sponsorisee</span>}
                </div>
                <p className="text-sm text-[#94A3B8] truncate">{c.title}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(c.placements || []).map(p => {
                    const pl = PLACEMENT_OPTIONS.find(x => x.id === p);
                    return pl ? (
                      <span key={p} className="text-[10px] bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] px-2 py-0.5 rounded-full">{pl.label}</span>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Stats summary */}
              <div className="hidden sm:flex items-center gap-6 text-center shrink-0">
                <div>
                  <p className="text-lg font-bold text-white">{globalImpressions}</p>
                  <p className="text-[10px] text-[#64748b]">Impressions</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">{globalClicks}</p>
                  <p className="text-[10px] text-[#64748b]">Clics</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[#0099CC]">{globalCtr}</p>
                  <p className="text-[10px] text-[#64748b]">CTR</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setStatsView(isExpanded ? null : c.id)} className={`p-2 rounded-lg hover:bg-white/5 transition-all ${isExpanded ? "text-[#0099CC]" : "text-[#94A3B8] hover:text-white"}`} title="Statistiques detaillees">
                  <BarChart3 className="h-4 w-4" />
                </button>
                <button onClick={() => toggleStatus(c)} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white transition-all" title={c.status === "active" ? "Mettre en pause" : "Activer"}>
                  {c.status === "active" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button onClick={() => handleEdit(c)} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white transition-all" title="Modifier">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400 transition-all" title="Supprimer">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Per-placement stats (expandable) */}
            {isExpanded && (
              <div data-testid="per-placement-stats" className="mt-4 pt-4 border-t border-[#2A3441]">
                <h4 className="text-xs font-mono uppercase tracking-wider text-[#64748b] mb-3">Statistiques par emplacement</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {(c.placements || []).map(p => {
                    const pl = PLACEMENT_OPTIONS.find(x => x.id === p);
                    const pStats = c.stats?.[p] || {};
                    const pImpressions = pStats.impressions || 0;
                    const pClicks = pStats.clicks || 0;
                    const pCtr = pImpressions > 0 ? ((pClicks / pImpressions) * 100).toFixed(1) + "%" : "—";
                    const Icon = pl?.icon || BarChart3;
                    return (
                      <div key={p} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Icon className="h-3.5 w-3.5 text-[#0099CC]" />
                          <span className="text-xs text-[#94A3B8] font-medium truncate">{pl?.label || p}</span>
                        </div>
                        <div className="flex gap-3 text-center">
                          <div>
                            <p className="text-sm font-bold text-white">{pImpressions}</p>
                            <p className="text-[9px] text-[#4A5568]">Impr.</p>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">{pClicks}</p>
                            <p className="text-[9px] text-[#4A5568]">Clics</p>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-[#0099CC]">{pCtr}</p>
                            <p className="text-[9px] text-[#4A5568]">CTR</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
